/**
 * Catálogo de permissões granulares (painel estilo HiDoctor).
 *
 * O DRAtento controla acesso por CARGO (Role). Este catálogo adiciona uma
 * camada de permissões finas por MÓDULO × AÇÃO em cima do cargo, editável na
 * tela Equipe. Cada cargo tem um conjunto PADRÃO (o que ele já pode hoje);
 * o dono/admin pode conceder a mais ou tirar por pessoa.
 *
 * Modelo "efetivo":
 *   - Se o profissional NÃO tem um mapa `permissions` salvo → usa o padrão do
 *     cargo (comportamento atual, sem regressão).
 *   - Se TEM → o mapa salvo é a verdade (pode conceder além ou revogar aquém
 *     do cargo).
 *
 * IMPORTANTE: ADMIN e o dono da clínica sempre têm acesso total, independentemente
 * do mapa — a checagem de "sempre liberado" para eles vive em `useRBAC` e nas
 * regras do Firestore, não aqui.
 */
import { Role } from '@/types/auth';
import type { Permissions } from '@/types/auth';

/** Ações no espírito do HiDoctor: Ver / Incluir / Alterar / Remover. */
export type PermissionAction = 'read' | 'create' | 'update' | 'delete';

export const ACTION_LABELS: Record<PermissionAction, string> = {
  read: 'Ver',
  create: 'Incluir',
  update: 'Alterar',
  delete: 'Remover',
};

export interface PermissionModule {
  /** Chave estável usada no mapa `permissions` e nas regras do Firestore. */
  key: string;
  /** Rótulo curto exibido na grade. */
  label: string;
  /** Descrição de uma linha (tooltip/ajuda). */
  description: string;
  /** Quais ações fazem sentido para este módulo (as demais aparecem "—"). */
  actions: PermissionAction[];
  /**
   * Ações concedidas por PADRÃO a cada cargo. Ausência = nenhuma.
   * Espelha o que o cargo já pode fazer hoje (regras do Firestore + menu).
   */
  defaults: Partial<Record<Role, PermissionAction[]>>;
}

const R = Role;

/**
 * Ordem de exibição na grade. Agrupa clínico → recepção → financeiro →
 * cadastros → administração.
 */
export const PERMISSION_MODULES: PermissionModule[] = [
  {
    key: 'patients',
    label: 'Dados do paciente',
    description: 'Cadastro do paciente (nome, contato, convênio).',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read', 'create', 'update'],
      [R.RECEPCIONISTA]: ['read', 'create'],
    },
  },
  {
    key: 'records',
    label: 'Prontuário / evoluções',
    description: 'Histórico clínico: evoluções, exames, anotações do médico.',
    actions: ['read', 'create', 'update', 'delete'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update', 'delete'],
      [R.MEDICO]: ['read', 'create', 'update', 'delete'],
    },
  },
  {
    key: 'clinicalImages',
    label: 'Imagens da ficha',
    description: 'Anexar e ver fotos/exames de imagem na ficha, sem o histórico clínico.',
    actions: ['read', 'create'],
    defaults: {
      [R.ADMIN]: ['read', 'create'],
      [R.MEDICO]: ['read', 'create'],
    },
  },
  {
    key: 'scheduling',
    label: 'Agenda / agendamentos',
    description: 'Marcar, remarcar e acompanhar consultas na agenda.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read', 'create', 'update'],
      [R.RECEPCIONISTA]: ['read', 'create', 'update'],
      [R.FINANCEIRO]: ['read'],
    },
  },
  {
    key: 'budgets',
    label: 'Orçamentos',
    description: 'Orçamentos e estimativas para o paciente.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read', 'create', 'update'],
      [R.FINANCEIRO]: ['read'],
    },
  },
  {
    key: 'tiss',
    label: 'Guias TISS',
    description: 'Guias de convênio (TISS) e seus lotes.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read', 'create', 'update'],
      [R.FINANCEIRO]: ['read'],
    },
  },
  {
    key: 'financial',
    label: 'Financeiro',
    description: 'Lançamentos financeiros, recebimentos e caixa.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.FINANCEIRO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read'],
    },
  },
  {
    key: 'inventory',
    label: 'Estoque',
    description: 'Itens de estoque e movimentações.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.FINANCEIRO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read'],
      [R.MEDICO]: ['read'],
    },
  },
  {
    key: 'catalogs',
    label: 'Cadastros',
    description: 'Medicamentos, biblioteca de textos e procedimentos.',
    actions: ['read', 'create', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'create', 'update'],
      [R.MEDICO]: ['read', 'create', 'update'],
      [R.SECRETARIA]: ['read'],
    },
  },
  {
    key: 'settings',
    label: 'Configurações',
    description: 'Configurações da clínica (impressão, agenda, padrões).',
    actions: ['read', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'update'],
      [R.MEDICO]: ['read', 'update'],
      [R.SECRETARIA]: ['read'],
      [R.RECEPCIONISTA]: ['read'],
      [R.FINANCEIRO]: ['read'],
    },
  },
  {
    key: 'team',
    label: 'Equipe',
    description: 'Convidar membros, trocar cargos e editar permissões.',
    actions: ['read', 'update'],
    defaults: {
      [R.ADMIN]: ['read', 'update'],
    },
  },
];

/** Lookup rápido por chave de módulo. */
export const PERMISSION_MODULE_BY_KEY: Record<string, PermissionModule> =
  Object.fromEntries(PERMISSION_MODULES.map((m) => [m.key, m]));

/**
 * Mapa de permissões PADRÃO de um cargo, derivado do catálogo.
 * `ROLE_BASE_PERMISSIONS[role][moduleKey][action] === true` quando o cargo
 * concede aquela ação por padrão.
 */
export const ROLE_BASE_PERMISSIONS: Record<Role, Permissions> = (() => {
  const out = {} as Record<Role, Permissions>;
  for (const role of Object.values(Role)) {
    const perms: Permissions = {};
    for (const mod of PERMISSION_MODULES) {
      const granted = mod.defaults[role] ?? [];
      const entry: Partial<Record<PermissionAction, boolean>> = {};
      for (const action of mod.actions) {
        entry[action] = granted.includes(action);
      }
      perms[mod.key] = entry;
    }
    out[role] = perms;
  }
  return out;
})();

/** True se o cargo concede a ação por PADRÃO (usado para marcar "vem do cargo"). */
export function isRoleDefault(
  role: Role,
  moduleKey: string,
  action: PermissionAction,
): boolean {
  return !!ROLE_BASE_PERMISSIONS[role]?.[moduleKey]?.[action];
}

/**
 * Permissões EFETIVAS de um profissional: o mapa salvo quando existe, senão o
 * padrão do cargo. Um mapa é considerado "existente" quando tem ao menos um
 * módulo com ao menos uma ação definida (evita que `{}` sobrescreva o cargo).
 */
export function effectivePermissions(
  role: Role,
  stored: Permissions | undefined | null,
): Permissions {
  if (!hasStoredPermissions(stored)) {
    return ROLE_BASE_PERMISSIONS[role] ?? {};
  }
  return stored as Permissions;
}

/** Um mapa `permissions` conta como "definido pelo usuário"? */
export function hasStoredPermissions(
  stored: Permissions | undefined | null,
): boolean {
  if (!stored) return false;
  return Object.values(stored).some(
    (mod) => mod && Object.values(mod).some((v) => v === true),
  );
}

/**
 * Decisão efetiva de UMA permissão (front-end). ADMIN/dono são tratados como
 * "sempre liberado" em `useRBAC`, não aqui.
 */
export function hasEffectivePermission(
  role: Role,
  stored: Permissions | undefined | null,
  moduleKey: string,
  action: PermissionAction,
): boolean {
  const eff = effectivePermissions(role, stored);
  return !!eff[moduleKey]?.[action];
}
